import React, { useState } from "react";
import styled from "styled-components";
import { useImmer } from "use-immer";
import uuid from "uuid/v4";
import validator from "validator";

import {
  assertAppSyncError,
  assertNativeError,
} from "@examspark/common/dist/utils";
import {
  useQueryGetOpenGraph,
  useMutationCreateExternalLink,
  useAppContext,
} from "@examspark/common-app";
import { Form, SubmitButtons, TextInput, toast } from "@examspark/common-ui";
import {
  SerializedExternalLink,
  ResourceVisibilityType,
  TagInput,
} from "@examspark/types";

import Modal, { ModalProps } from "../common/Modal";

const CoverImage = styled.img`
  border-radius: 0.5rem;
  object-fit: cover;
  max-height: 200px;
`;

const NewLinkModal = ({
  classroomId,
  courseId,
  unitId,
  visibility,
  onComplete,
  ...props
}: ModalProps & {
  classroomId?: string;
  courseId?: string;
  unitId?: string;
  visibility?: ResourceVisibilityType;
  onComplete?: (link?: SerializedExternalLink) => void;
}) => {
  const [context] = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [form, updateForm] = useImmer<{
    name?: string;
    description?: string;
    image?: string;
  } | null>(null);

  const getOpenGraphMetadata = useQueryGetOpenGraph();
  const createExternalLink = useMutationCreateExternalLink();
  const urlRef = React.useRef<HTMLInputElement>(null);

  const handleSubmitToOpenGraph = async () => {
    try {
      if (!validator.isURL(url)) {
        throw new Error("Invalid URL");
      }

      setIsLoading(true);
      try {
        const result = await getOpenGraphMetadata.mutateAsync(url);

        if (!result) {
          toast.error(
            "Could not automatically fetch page data. Please continue manually or try again later.",
          );
          updateForm(() => {
            return {};
          });

          return;
        }

        updateForm((draft) => {
          return {
            name: result.title,
            description: result.description,
            image: result.imageSecureUrl || result.image,
          };
        });
      } catch (error) {
        assertAppSyncError(error);
        if (error.errors[0].errorType === "MissingParameter") {
          alert("Invalid URL");
        } else {
          alert(error.errors[0].message);
        }
      } finally {
        setIsLoading(false);
      }
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
    }
  };

  const handleCreateResource = async () => {
    if (!form) return;

    if (!url) {
      alert("URL is required");
      return;
    }

    if (!form.name) {
      alert("Title is required");
      return;
    }

    setIsLoading(true);

    try {
      const tags: TagInput[] = [];
      if (classroomId) {
        tags.push({ key: "classroom-id", type: "string", value: classroomId });
      }
      if (courseId) {
        tags.push({ key: "course-id", type: "string", value: courseId });
      }
      if (unitId) {
        tags.push({ key: "unit-id", type: "string", value: unitId });
      }

      const createLinkResult = await createExternalLink.mutateAsync({
        id: uuid(),
        sk: "resource#v0",
        schemaVersion: "2020-01-01",
        type: "EXTERNAL_LINK",
        name: form.name,
        description: form.description,
        workspaceId: context.workspaceUser?.id,
        bankId: context.appLicenseConfig?.userContentBankId,
        creatorId: context.user?.id,
        imageUrl: form.image,
        maturity: "OPERATIONAL",
        visibility: visibility ?? ResourceVisibilityType.Private,
        url: url,
        data: {
          url,
          title: form.name,
          description: form.description,
          image: form.image,
        },
        tags,
      });

      onComplete?.(createLinkResult);
      setIsLoading(false);
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="New Web Link"
      size="medium"
      onAfterClose={() => {
        setIsLoading(false);
        setUrl("");
        updateForm(() => {
          return null;
        });
      }}
      onAfterOpen={() => {
        urlRef.current?.focus();
      }}
      buttons={
        <>
          {!form ? (
            <SubmitButtons
              isSubmitting={isLoading}
              submitLabel="Next"
              onSubmit={handleSubmitToOpenGraph}
              onCancel={props.onRequestClose}
            />
          ) : (
            <SubmitButtons
              isSubmitting={isLoading}
              submitLabel="Add Link to Resources"
              onSubmit={handleCreateResource}
              onCancel={props.onRequestClose}
            />
          )}
        </>
      }
      {...props}
    >
      <Modal.PaddedBody>
        <Form.FieldList>
          <Form.Field label="Website URL *">
            <TextInput
              value={url}
              ref={urlRef}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://youtube.com/xis8d6ds"
              onEnterKeyPress={() => {
                if (form) {
                  handleCreateResource();
                  return;
                }
                handleSubmitToOpenGraph();
              }}
            />
          </Form.Field>
          {!!form && (
            <>
              <Form.Field label="Title *">
                <TextInput
                  value={form.name}
                  onChange={(e) =>
                    updateForm((draft) => {
                      if (!draft) return;
                      draft.name = e.target.value;
                    })
                  }
                />
              </Form.Field>
              <Form.Field label="Description">
                <TextInput
                  value={form.description}
                  onChange={(e) =>
                    updateForm((draft) => {
                      if (!draft) return;
                      draft.description = e.target.value;
                    })
                  }
                />
              </Form.Field>
              {form.image && (
                <Form.Field label="Cover Image">
                  <CoverImage src={form.image} alt="Weblink Thumbnail" />
                </Form.Field>
              )}
            </>
          )}
        </Form.FieldList>
      </Modal.PaddedBody>
    </Modal>
  );
};

export default NewLinkModal;
